import { StripeCardCvcElement, StripeCardElement, StripeCardExpiryElement } from '@stripe/stripe-js';
import { IntercomService } from '@teammood/shared/util/intercom';
import { StripeService } from '@teammood/shared/util/stripe';
import { UserService } from 'app/frontend/lib/shared/util/user';
import { Countries, Country } from './country.type';
import { SnackBarService } from "@teammood/shared/ui/snack-bar";
import {
    AUDCurrency,
    CADCurrency,
    Currency,
    EuroCurrency,
    GBPCurrency,
    MonthlyBillableAmount,
    SubscriptionTeam,
    USDCurrency
} from "../billing.type";
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { BillingService } from "../billing.service";
import { LoggerService } from "@teammood/shared/util/logger";
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    EMPTY,
    filter,
    forkJoin,
    mergeMap,
    Observable,
    Subject,
    switchMap,
    takeUntil,
    tap
} from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";

@Component({
    selector: "add-credit-card",
    templateUrl: "./credit-card.component.html",
    styleUrls: ["./credit-card.component.scss"],
    providers: [
        StripeService,
        BillingService
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CreditCardComponent implements OnInit, OnDestroy {

    countries: Array<Country> = Countries;

    protected currencyCtrl: FormControl<Currency> = new FormControl<Currency>(EuroCurrency, [Validators.required]);

    teamId: string;

    processing_step: string = '';
    loading: boolean = false;
    within_eu: boolean = false;

    protected nameCtrl: FormControl<string> = new FormControl<string>('', [Validators.required]);
    protected addressLine1Ctrl: FormControl<string> = new FormControl<string>('', [Validators.required]);
    protected addressLine2Ctrl: FormControl<string> = new FormControl<string>('');
    protected addressCityCtrl: FormControl<string> = new FormControl<string>('', [Validators.required]);
    protected postalcodeCtrl: FormControl<string> = new FormControl<string>('', [Validators.required]);
    protected selectedCountryCtrl: FormControl<Country> = new FormControl<Country>(null, [Validators.required]);
    protected vatNumberCtrl: FormControl<string> = new FormControl<string>(null);

    protected billingForm: FormGroup = new FormGroup({
        name: this.nameCtrl,
        addressLine1: this.addressLine1Ctrl,
        addressLine2: this.addressLine2Ctrl,
        addressCity: this.addressCityCtrl,
        postalcode: this.postalcodeCtrl,
        selectedCountry: this.selectedCountryCtrl,
        vat_number: this.vatNumberCtrl
    });

    cardNumber: StripeCardElement;
    private cardNumberValid: boolean = false;
    private cardExpiryValid: boolean = false;
    private cardCVCValid: boolean = false;

    teams: Array<SubscriptionTeam>;
    vat_rate: number;
    vat_amount: number;
    unit_price: number;
    total_active_members_count: number;
    duplicated_members_count: number;
    total_amount: number;

    private _destroy$ = new Subject<void>();

    constructor(
        private logger: LoggerService,
        private $translate: TranslateService,
        private snackBarService: SnackBarService,
        private billingService: BillingService,
        private stripeService: StripeService,
        private userService: UserService,
        private intercomService: IntercomService,
        private router: Router,
        private route: ActivatedRoute,
    ) { }

    ngOnInit() {

        const france: Country = { value: 'FR', label: 'France' };

        this.route.params
            .pipe(
                mergeMap(params => {
                    if (!params['teamId']) {
                        return this.router.navigate(['/login']);
                    } else {
                        this.teamId = params['teamId'];

                        this.userService.get(); // start Intercom

                        this.cardNumber = this.stripeService.createCardNumberElement('#card-number') as StripeCardElement;
                        const expiry = this.stripeService.createCardExpiryElement('#card-expiry') as StripeCardExpiryElement;
                        const cvc = this.stripeService.createCardCvcElement('#card-cvc') as StripeCardCvcElement;

                        this.cardNumber.on('change', (event) => {
                            this.cardNumberValid = event.complete;
                        });

                        expiry.on('change', (event) => {
                            this.cardExpiryValid = event.complete;
                        });

                        cvc.on('change', (event) => {
                            this.cardCVCValid = event.complete;
                        });

                        return this.billingService.get_subscription(this.teamId).pipe(
                            tap((response: any) => {
                                if (response.status !== 204) {
                                    this.snackBarService.error(this.$translate.instant('already_subscribed'));
                                }
                            }),
                            catchError(err => {
                                this.logger.error('error checking if already subscribed', err);
                                return EMPTY;
                            }),
                        )
                    }
                }),

                mergeMap(() => {

                    return forkJoin([
                        this.getBillableDetails(france), //init teams
                        this.currencyCtrl.valueChanges.pipe(
                            switchMap(() => {
                                return this.getBillableDetails(this.selectedCountryCtrl.value || france)
                            })
                        ),
                        this.selectedCountryCtrl.valueChanges.pipe(
                            this.updateBillingDetails(),
                        ),
                        this.vatNumberCtrl.valueChanges.pipe(
                            debounceTime(500),
                            distinctUntilChanged(),
                            filter(vatNumber => !!vatNumber),
                            switchMap(() => {
                                return this.getBillableDetails(this.selectedCountryCtrl.value)
                            }),
                        )
                    ]);
                }),
                takeUntil(this._destroy$)
            ).subscribe();

    }

    stripePaymentFormValid(){
        return this.cardNumberValid && this.cardExpiryValid && this.cardCVCValid
    }

    private updateBillingDetails() {
        return switchMap((selectedCountry: Country) => {
            return forkJoin([
                this.isInEU(selectedCountry),
                this.getBillableDetails(selectedCountry)
            ])
        })
    }

    private isInEU(selectedCountry: Country): Observable<{ isInEu: boolean }> {
        const countryCode = selectedCountry.value;

        return this.billingService.isInEU(this.teamId, countryCode).pipe(
            tap((success: { isInEu: boolean }) => {
                this.within_eu = success.isInEu;
                if (this.within_eu) {
                    this.vatNumberCtrl.setValidators([Validators.required]);
                } else {
                    this.vatNumberCtrl.setValidators([]);
                }
                this.vatNumberCtrl.updateValueAndValidity({emitEvent: false});
            }),
            catchError((error: any) => {
                this.logger.error('can\'t check if in EU', error);
                this.within_eu = false;
                return EMPTY;
            })
        )
    }

    private getBillableDetails(selectedCountry: Country): Observable<MonthlyBillableAmount> {
        const countryCode = selectedCountry.value;

        return this.billingService.get_billable_details(this.teamId, countryCode, this.currencyCtrl.value.id, this.vatNumberCtrl.value).pipe(
            tap((response: MonthlyBillableAmount) => {
                this.teams = response.teams;
                this.unit_price = response.unit_price;
                this.vat_rate = response.vat_rate;
                this.vat_amount = response.vat_amount;
                this.total_active_members_count = response.total_active_members_count;
                this.duplicated_members_count = response.duplicated_members_count;
                this.total_amount = response.total_amount;
            }),
            catchError(err => {
                this.logger.error('error getting billibable details for payment', err);
                return EMPTY;
            })
        )
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    submitPaymentForm () {

        this.loading = true;
        this.processing_step = "1/3";

        const customerAddress = {
            name: this.nameCtrl.value,
            addressLine1: this.addressLine1Ctrl.value,
            addressLine2: this.addressLine2Ctrl.value,
            addressCity: this.addressCityCtrl.value,
            addressZip: this.postalcodeCtrl.value,
            countryCode: this.selectedCountryCtrl.value?this.selectedCountryCtrl.value.value:''
        };

        this.userService.get().then(user => {
            this.stripeService.createPaymentMethod(this.cardNumber, user.email, customerAddress).then(data => {
                if (data.error) {
                    this.snackBarService.error(data.error.message);
                    this.loading = false;
                    this.processing_step = '';
                } else {
                    this.logger.info("Payment method validated");
                    this.createSubscription(data.paymentMethod.id, customerAddress);
                }
            }).catch(error => {
                this.logger.error("billing error", error);
                this.snackBarService.error(error.message);
                this.loading = false;
                this.processing_step = "";
            })
        })
    }

    createSubscription (paymentMethodId: string, customerAddress: any) {
        this.loading = true;
        this.processing_step = "2/3";
        this.logger.info("billing in progress, card info validated by Stripe, starting subscription via the backend");

        this.stripeService.createCustomerAndSubscription(paymentMethodId, this.teamId, this.currencyCtrl.value.id, this.vatNumberCtrl.value, customerAddress)
            .then( (success) => {
                if (success.status === 202) { // 3D Secure needed
                    this.stripeService.start3DSecure(success.body['client_secret']).then(async result => {
                        if (result.error) {
                            this.logger.error("3D Secure error", result);
                            this.snackBarService.error("3D Secure error: " + this.$translate.instant('credit_card_3d_secure_error'));
                            await this.router.navigate(['/setup/credit_card/paymentfailed/', this.teamId]);
                        } else {
                            this.logger.info("billing in progress, 3D Secure validated");
                            this.subscriptionValidated();
                        }
                    });
                } else { // payment ok
                    this.logger.info("billing validated!!");
                    this.subscriptionValidated();
                }
            }).catch((httpError) => {
                this.logger.error("billing error", httpError);
                if (httpError.error.error && httpError.error.error.type) {
                    this.snackBarService.error('[' + httpError.error.error.type + '] ' + httpError.error.error.message);
                } else {
                    this.snackBarService.error(httpError);
                }
                this.loading = false;
            });
    }

    subscriptionValidated () {
        this.processing_step = "3/3";
        this.snackBarService.success(this.$translate.instant("subscription_validated"));
        setTimeout(async () => {
            this.loading = false;
            await this.router.navigate(['/setup/credit_card/paymentvalidated', this.teamId]);
        }, 2000);
    }

    chat () {
        this.intercomService.open_chat("Billing question");
    }

    protected readonly EuroCurrency = EuroCurrency;
    protected readonly CADCurrency = CADCurrency;
    protected readonly AUDCurrency = AUDCurrency;
    protected readonly USDCurrency = USDCurrency;
    protected readonly GBPCurrency = GBPCurrency;
}
